/**
 * Commander un article, d'un seul accord : créer la commande et l'envoyer.
 *
 * Jusqu'ici, deux gestes séparés par un écran : `passer_commande_materiaux`
 * (N2) remplit le panier et crée la commande, `envoyer_commande_materiaux` (N3)
 * la met dans les mains du fournisseur. La file du soir voyait « Commander
 * l'enduit » et ne pouvait qu'ouvrir le catalogue — le troisième des verbes
 * que le plan UI/UX demandait.
 *
 * **La quantité reste au patron.** C'est la décision du lot 1, et elle tient :
 * commander « de quoi repasser le seuil » serait un chiffre inventé, et c'est
 * un achat. L'écran demande la quantité, le verbe fait le reste.
 *
 * Ce que le résumé promet est vérifié avant l'envoi. La commande est créée au
 * prix du catalogue **au moment de l'exécution**, par la base ; si ce prix, ou
 * le fournisseur de l'article, a changé depuis la préparation, la commande
 * reste créée et n'est pas envoyée. Le patron a confirmé 240 € chez un
 * fournisseur ; on n'envoie pas 310 € chez un autre.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { DocumentPort, DocumentSendReceipt } from "../documents/services.js";
import type { MaterialOrdersRepository } from "./services.js";

export const orderStockItemInput = z.object({
  confirmationId: z.string().optional(),
  catalogItemId: z.string().uuid("Identifiant d'article attendu"),
  quantity: z.number().positive("La quantité doit être positive"),
  /** Tout ce qui suit sert au résumé et à la vérification — la base relit l'article. */
  itemName: z.string().trim().min(1),
  unit: z.string().trim().min(1),
  supplierId: z.string().uuid("Cet article n'a pas de fournisseur"),
  supplierName: z.string().trim().min(1),
  supplierEmail: z.string().email("Adresse e-mail du fournisseur invalide"),
  totalHt: z.number(),
});

export type OrderStockItemInput = z.infer<typeof orderStockItemInput>;

export interface StockItemOrdered extends DocumentSendReceipt {
  orderId: string;
}

const eur = (n: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);

const nombre = (n: number) => new Intl.NumberFormat("fr-FR").format(n);

/** Le total d'une ligne, arrondi comme `create_material_orders_from_basket` l'arrondit. */
export function orderLineTotal(unitCost: number, quantity: number): number {
  return Math.round(unitCost * quantity * 100) / 100;
}

export function createOrderingServices(deps: {
  orders: MaterialOrdersRepository;
  documents: DocumentPort;
}) {
  const commanderMateriel = defineService<OrderStockItemInput, StockItemOrdered>({
    name: "commander_materiel",
    level: ActionLevel.ExternalEffect,
    summarize: (i) =>
      `Commander ${nombre(i.quantity)} ${i.unit} de ${i.itemName} chez ${i.supplierName} — ` +
      `${i.supplierEmail}, soit ${eur(i.totalHt)} HT. Le bon de commande part au ` +
      `fournisseur et la commande passe au statut « commandée ».`,
    handler: async (ctx, input) => {
      const commandes = await deps.orders.createFromBasket(
        ctx,
        [{ catalogItemId: input.catalogItemId, quantity: input.quantity }],
        null,
        null,
      );
      const commande = commandes[0];
      if (!commande || commandes.length !== 1) {
        throw new DomainError("conflict", "La commande n'a pas pu être créée.", {
          catalogItemId: input.catalogItemId,
        });
      }

      const memeFournisseur = commande.supplierId === input.supplierId;
      const memePrix = Math.abs(commande.totalHt - input.totalHt) <= 0.01;
      if (!memeFournisseur || !memePrix) {
        throw new DomainError(
          "conflict",
          (memeFournisseur
            ? `Le prix de l'article a changé depuis la préparation : ${eur(commande.totalHt)} HT ` +
              `au lieu de ${eur(input.totalHt)}.`
            : "Le fournisseur de l'article a changé depuis la préparation.") +
            " La commande est créée mais n'est pas partie : elle vous attend dans Commandes.",
          { orderId: commande.id },
        );
      }

      try {
        const envoi = await deps.documents.sendMaterialOrder(ctx, { orderId: commande.id });
        return { ...envoi, orderId: commande.id };
      } catch (e) {
        const cause = e instanceof Error ? e.message : "erreur inconnue";
        throw new DomainError(
          "conflict",
          `La commande est créée mais n'est pas partie : ${cause} ` +
            "Elle vous attend dans Commandes, d'où elle s'envoie.",
          { orderId: commande.id },
        );
      }
    },
  });

  return { commanderMateriel };
}

export type OrderingServices = ReturnType<typeof createOrderingServices>;
